import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer
} from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import { HTML5Backend } from "react-dnd-html5-backend";

import inputConnect from "../inputConnect";
import {
  ADD_TO_SELECTED_ITEMS,
  ADD_ITEM_TO_OPTIONS,
  MOVE_SELECTED_ITEMS,
  MOVE_OPTIONS,
  REMOVE_FROM_SELECTED_ITEMS,
  REMOVE_ITEM_FROM_OPTIONS,
  INITIALIZE,
  SortableListActions
} from "./SortableListActions";
import { getIsTouchDevice } from "../../Utils/ResponsiveQueries";
import SortableListDesktopInterface from "./SortableListDesktopInterface";
import SortableListTouchScreenInterface from "./SortableListTouchScreenInterface";

const initialState = {
  options: [],
  selectedItems: []
};

const SortableListInputContext = createContext(initialState);

export const useSortableListInputContext = () => useContext(SortableListInputContext);

const SortableListInput = props => {
  const {
    options: inputOptions,
    updateValue,
    onChange,
    values,
    label,
    maximumOptions
  } = props;

  const isTouchDevice = getIsTouchDevice();

  const dndBackend = useMemo(
    () => (isTouchDevice ? TouchBackend : HTML5Backend),
    [isTouchDevice]
  );

  const [optionsState, dispatch] = useReducer(SortableListActions, {
    ...initialState,
    updateValue,
    onChange
  });

  const addToSelectedItems = useCallback(droppedItem => {
    dispatch({ type: ADD_TO_SELECTED_ITEMS, payload: { droppedItem } });
    // When dropping an item from the source list, we need to remove it from that list
    dispatch({ type: REMOVE_ITEM_FROM_OPTIONS, payload: { droppedItem } });
  }, []);

  const addItemToOptions = useCallback(droppedItem => {
    dispatch({
      type: REMOVE_FROM_SELECTED_ITEMS,
      payload: { itemToRemove: droppedItem }
    });
    dispatch({ type: ADD_ITEM_TO_OPTIONS, payload: { droppedItem } });
  }, []);

  const moveSelectedItems = useCallback((draggedItem, sourceIndex, targetIndex) => {
    dispatch({
      type: MOVE_SELECTED_ITEMS,
      payload: { draggedItem, sourceIndex, targetIndex }
    });
  }, []);

  const moveOptions = useCallback((draggedItem, sourceIndex, targetIndex) => {
    dispatch({
      type: MOVE_OPTIONS,
      payload: { draggedItem, sourceIndex, targetIndex }
    });
  }, []);

  // Define options and filled values in the store when the component is mounted
  useEffect(() => {
    dispatch({ type: INITIALIZE, payload: { inputOptions, inputValues: values } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({
      ...optionsState,
      inputOptions,
      addToSelectedItems,
      addItemToOptions,
      moveSelectedItems,
      moveOptions,
      label,
      maximumOptions
    }),
    [
      addItemToOptions,
      addToSelectedItems,
      inputOptions,
      label,
      maximumOptions,
      moveOptions,
      moveSelectedItems,
      optionsState
    ]
  );

  return (
    <DndProvider backend={dndBackend} options={{ enableMouseEvents: true }}>
      <SortableListInputContext.Provider value={contextValue}>
        {isTouchDevice && <SortableListTouchScreenInterface />}
        {!isTouchDevice && <SortableListDesktopInterface />}
      </SortableListInputContext.Provider>
    </DndProvider>
  );
};

export default inputConnect(SortableListInput, {
  fields: { values: [], optionsCount: 0 }
});
